import { useEffect, useState } from 'react';
import appConfigApi from '../../services/AppConfigDeployer/AppConfigDeployerService';

export interface AppConfigAccess {
    /** undefined while loading, then true/false. */
    hasAccess: boolean | undefined;
    loading: boolean;
}

/**
 * Calls GET /appconfig-api/access on mount (and when stage/region/alias change) to determine
 * whether the current user has access to the AppConfig Deployer. Returns
 * `hasAccess === false` when the user is denied or when the call fails (fail-closed).
 *
 * Use this to gate the AppConfig Deployer UI so users without access don't see it at all.
 */
export function useAppConfigAccess(stage: string, region: string, userAlias: string): AppConfigAccess {
    const [hasAccess, setHasAccess] = useState<boolean | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        // Treat unauthenticated / placeholder users as denied without making a request.
        if (!userAlias || userAlias === 'Guest') {
            setHasAccess(false);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setHasAccess(undefined);
        appConfigApi.checkAccess(stage, region)
            .then((allowed) => {
                if (!cancelled) {
                    setHasAccess(allowed);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setHasAccess(false);
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [stage, region, userAlias]);

    return { hasAccess, loading };
}
